import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { encryptFilters } from "../utils/encryptUtils";
import { useAuth } from "../context/authContext";
import { getSriLankaToday, getSriLankaDaysAgo } from "../utils/dateUtils";
import TeamFloatingNavigation from "../components/TeamFloatingNavigation";

function ViewSalePage() {
  const { token } = useAuth();
  const [sales, setSales] = useState([]);
  const [agents, setAgents] = useState([]);
  const [rowLimit, setRowLimit] = useState(20);
  const [dateFilter, setDateFilter] = useState("Today");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [advanceFilterEnabled, setAdvanceFilterEnabled] = useState(false);
  const [agentName, setAgentName] = useState("");
  const [customerType, setCustomerType] = useState("");
  const [bookingType, setBookingType] = useState("");
  const [showComments, setShowComments] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalTickets, setTotalTickets] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLink, setShareLink] = useState("");
  const [showUpdates, setShowUpdates] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [showAddSaleModal, setShowAddSaleModal] = useState(false);
  
  // Guide selection states
  const [guides, setGuides] = useState([]);
  const [selectedGuide, setSelectedGuide] = useState("");
  const [guideSearchTerm, setGuideSearchTerm] = useState("");
  const [showAddGuideModal, setShowAddGuideModal] = useState(false);
  const [deleteConfirmCode, setDeleteConfirmCode] = useState("");
  const [guidesLoading, setGuidesLoading] = useState(false);

  const fetchSales = async () => {
    try {
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/get-sales?`;
      const today = getSriLankaToday();

      if (dateFilter === "Today") {
        url += `date=${today}`;
      } else if (dateFilter === "Last 7 Days") {
        const last7 = getSriLankaDaysAgo(6); // today + previous 6 days
        url += `startDate=${last7}&endDate=${today}`;
      } else if (dateFilter === "Last 30 Days") {
        const last30 = getSriLankaDaysAgo(29); // today + previous 29 days
        url += `startDate=${last30}&endDate=${today}`;
      } else if (dateFilter === "Custom Date" && customStartDate) {
        url += `date=${customStartDate}`;
      } else if (
        dateFilter === "Custom Range" &&
        customStartDate &&
        customEndDate
      ) {
        url += `startDate=${customStartDate}&endDate=${customEndDate}`;
      }

      if (advanceFilterEnabled) {
        if (agentName) url += `&agentName=${agentName}`;
        if (customerType) url += `&customerType=${customerType}`;
        if (bookingType) url += `&bookingType=${bookingType}`;
        if (paymentMethod) url += `&paymentMethod=${paymentMethod}`;
      }

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSales(response.data);
      calculateTotals(response.data);
    } catch (error) {
      console.error("Error fetching sales:", error.message);
      if (
        error.response?.status === 400 &&
        error.response?.data?.message?.includes("No site specified")
      ) {
        console.warn("Team user accessing sales - may need site assignment");
      }
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/get-users?role=Agent`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setAgents(response.data);
    } catch (error) {
      console.error("Error fetching agents:", error.message);
    }
  };

  const fetchGuides = useCallback(async () => {
    try {
      setGuidesLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/get-guides`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      // Ensure we have a valid array
      const guidesData = Array.isArray(response.data) ? response.data : [];
      setGuides(guidesData);
    } catch (error) {
      console.error("Error fetching guides:", error.message);
      // Set empty array on error to prevent undefined issues
      setGuides([]);
    } finally {
      setGuidesLoading(false);
    }
  }, [token]);

  const calculateTotals = (data) => {
    let totalPriceSum = 0;
    let totalTicketsSum = 0;

    data.forEach((sale) => {
      totalPriceSum += sale.totalPrice;
      totalTicketsSum += sale.numberOfTickets;
    });

    setTotalPrice(totalPriceSum);
    setTotalTickets(totalTicketsSum);
  };

  const handleExportCSV = () => {
    if (sales.length === 0) return;

    const filteredSales = sales.map((sale) => {
      const base = {
        Agent: sale.salesAgent,
        BookingType: sale.bookingType,
        Tickets: sale.numberOfTickets,
        TotalPrice: sale.totalPrice,
        PaymentMethod: sale.paymentMethod,
        CustomerType: sale.customerType,
        Date: sale.date,
        Time: sale.time,
      };
      if (showComments) {
        base.Comments = sale.comments || "";
      }
      return base;
    });

    const csvRows = [];
    const headers = Object.keys(filteredSales[0]);
    csvRows.push(headers.join(","));

    for (const row of filteredSales) {
      const values = headers.map((header) => `"${row[header]}"`);
      csvRows.push(values.join(","));
    }

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "sales-data.csv");
  };

  const handleExportXLSX = () => {
    if (sales.length === 0) return;

    const filteredSales = sales.map((sale) => {
      const base = {
        Agent: sale.salesAgent,
        BookingType: sale.bookingType,
        Tickets: sale.numberOfTickets,
        TotalPrice: sale.totalPrice,
        PaymentMethod: sale.paymentMethod,
        CustomerType: sale.customerType,
        Date: sale.date,
        Time: sale.time,
      };
      if (showComments) {
        base.Comments = sale.comments || "";
      }
      return base;
    });

    const worksheet = XLSX.utils.json_to_sheet(filteredSales);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sales");

    const xlsxBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([xlsxBuffer], { type: "application/octet-stream" });
    saveAs(blob, "sales-data.xlsx");
  };

  useEffect(() => {
    if (token) {
      fetchAgents();
      fetchGuides();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchSales();
    }
    // eslint-disable-next-line
  }, [
    token,
    dateFilter,
    customStartDate,
    customEndDate,
    advanceFilterEnabled,
    agentName,
    customerType,
    bookingType,
    paymentMethod,
  ]);

  // Handle messages from iframe modals
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'GUIDE_ADDED') {
        setShowAddGuideModal(false);
        fetchGuides(); // Refresh the guides list
        setGuideSearchTerm(""); // Clear search
        alert("Guide added successfully! The guides list has been refreshed.");
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [fetchGuides]);

  const handleShare = () => {
    const filters = {
      dateFilter,
      customStartDate,
      customEndDate,
      advanceFilterEnabled,
      agentName,
      customerType,
      bookingType,
      paymentMethod,
      showComments,
    };

    const encrypted = encryptFilters(filters);
    const url = `${window.location.origin}/share/${encodeURIComponent(
      encrypted
    )}`;
    setShareLink(url);
    setShowShareModal(true);
  };

  const handleRowClick = (sale) => {
    setSelectedSale({ ...sale });
    setSelectedGuide(sale.guideName || "");
    setDeleteConfirmCode("");
    setShowUpdateModal(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Page Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-slate-800 mb-2">Sales Overview</h1>
          <p className="text-slate-600">Manage and analyze your sales data</p>
        </div>

        {/* Totals Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">Total Sales</h3>
                <p className="text-3xl font-bold text-slate-800">{totalTickets}</p>
                <p className="text-sm text-slate-500 mt-1">Tickets sold</p>
              </div>
              <div className="bg-blue-500 p-3 rounded-full">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">Total Revenue</h3>
                <p className="text-3xl font-bold text-slate-800">LKR {totalPrice.toLocaleString()}</p>
                <p className="text-sm text-slate-500 mt-1">Total earnings</p>
              </div>
              <div className="bg-emerald-500 p-3 rounded-full">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center justify-center">
              <button
                onClick={() => setShowAddSaleModal(true)}
                className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center gap-3"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add New Sale
              </button>
            </div>
          </div>
        </div>

        {/* Filters Panel */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 mb-8 overflow-hidden">
          <div className="bg-gradient-to-r from-slate-50 to-slate-100 px-6 py-4 border-b border-slate-200">
            <h2 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
              <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
              </svg>
              Filters & Options
            </h2>
          </div>
          
          <div className="p-6">
            {/* Primary Filters Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-6 mb-6">
              
              {/* Rows Filter */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">Display Rows</label>
                <select
                  value={rowLimit}
                  onChange={(e) => setRowLimit(e.target.value === "All" ? "All" : Number(e.target.value))}
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm bg-white shadow-sm transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 hover:border-slate-400"
                >
                  <option value={20}>20 rows</option>
                  <option value={50}>50 rows</option>
                  <option value={100}>100 rows</option>
                  <option value="All">All rows</option>
                </select>
              </div>

              {/* Date Filter */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">Date Range</label>
                <select
                  value={dateFilter}
                  onChange={(e) => {
                    setDateFilter(e.target.value);
                    setCustomStartDate("");
                    setCustomEndDate("");
                  }}
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm bg-white shadow-sm transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 hover:border-slate-400"
                >
                  <option value="Today">Today</option>
                  <option value="Last 7 Days">Last 7 Days</option>
                  <option value="Last 30 Days">Last 30 Days</option>
                  <option value="Custom Date">Custom Date</option>
                  <option value="Custom Range">Date Range</option>
                </select>
              </div>

              {/* Custom Date Inputs */}
              {dateFilter === "Custom Date" && (
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-slate-700">Select Date</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm bg-white shadow-sm transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 hover:border-slate-400"
                  />
                </div>
              )}

              {dateFilter === "Custom Range" && (
                <>
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-slate-700">Start Date</label>
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm bg-white shadow-sm transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 hover:border-slate-400"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-slate-700">End Date</label>
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm bg-white shadow-sm transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 hover:border-slate-400"
                    />
                  </div>
                </>
              )}

              {/* Advanced Filters Toggle */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">Advanced Filters</label>
                <div className="flex items-center justify-center h-12">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={advanceFilterEnabled}
                      onChange={(e) => setAdvanceFilterEnabled(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    <span className="ml-3 text-sm font-medium text-slate-600">Enable</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Advanced Filters Section */}
            {advanceFilterEnabled && (
              <div className="border-t border-slate-200 pt-6 mt-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">Advanced Filters</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-slate-700">Sales Agent</label>
                    <select
                      value={agentName}
                      onChange={(e) => setAgentName(e.target.value)}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm bg-white shadow-sm transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 hover:border-slate-400"
                    >
                      <option value="">All Agents</option>
                      {agents.map((agent) => (
                        <option key={agent._id} value={agent.name}>
                          {agent.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-slate-700">Customer Type</label>
                    <select
                      value={customerType}
                      onChange={(e) => setCustomerType(e.target.value)}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm bg-white shadow-sm transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 hover:border-slate-400"
                    >
                      <option value="">All Customer Types</option>
                      <option value="Foreigner">Foreigner</option>
                      <option value="Local">Local</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-slate-700">Payment Method</label>
                    <select
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm bg-white shadow-sm transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 hover:border-slate-400"
                    >
                      <option value="">All Payment Methods</option>
                      <option value="Cash">Cash</option>
                      <option value="Card">Card</option>
                      <option value="Cash & Card">Cash & Card</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-slate-700">Booking Type</label>
                    <select
                      value={bookingType}
                      onChange={(e) => setBookingType(e.target.value)}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm bg-white shadow-sm transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 hover:border-slate-400"
                    >
                      <option value="">All Booking Types</option>
                      <option value="Walk-in (approach us)">Walk-in (approach us)</option>
                      <option value="Walk-in (approach by team)">Walk-in (approach by team)</option>
                      <option value="Online Booking">Online Booking</option>
                      <option value="Brought by Guide">Brought by Guide</option>
                      <option value="In-house">In-house</option>
                      <option value="Restaurant">Restaurant</option>
                      <option value="OTA">OTA</option>
                      <option value="DMC">DMC</option>
                      <option value="Other (Shops/Restaurants)">Other (Shops/Restaurants)</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Display Options & Actions */}
            <div className="border-t border-slate-200 pt-6 mt-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                
                {/* Display Options */}
                <div className="flex flex-wrap gap-6">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showComments}
                      onChange={(e) => setShowComments(e.target.checked)}
                      className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <span className="text-sm font-semibold text-slate-700">Show Comments</span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showUpdates}
                      onChange={(e) => setShowUpdates(e.target.checked)}
                      className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <span className="text-sm font-semibold text-slate-700">Show Update History</span>
                  </label>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={handleExportCSV}
                    className="flex items-center gap-2 px-6 py-3 bg-white border border-slate-300 text-slate-700 rounded-xl hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export CSV
                  </button>
                  
                  <button
                    onClick={handleExportXLSX}
                    className="flex items-center gap-2 px-6 py-3 bg-white border border-slate-300 text-slate-700 rounded-xl hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a4 4 0 01-4-4V5a4 4 0 014-4h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a4 4 0 01-4 4z" />
                    </svg>
                    Export Excel
                  </button>
                  
                  <button
                    onClick={handleShare}
                    className="flex items-center gap-2 px-6 py-3 bg-blue-500 border border-blue-500 text-white rounded-xl hover:bg-blue-600 hover:border-blue-600 transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                    </svg>
                    Share View
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sales Table */}
        {sales.length > 0 ? (
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-slate-50 to-slate-100 px-6 py-4 border-b border-slate-200">
              <h2 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
                <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Sales Data ({rowLimit === "All" ? sales.length : Math.min(rowLimit, sales.length)} entries)
              </h2>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Agent</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Booking Type</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Invoice No</th>
                    <th className="px-6 py-4 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">Tickets</th>
                    <th className="px-6 py-4 text-right text-xs font-bold text-slate-600 uppercase tracking-wider">Total Amount</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Payment</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Customer</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Time</th>
                    {showComments && <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Comments</th>}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-100">
                  {(rowLimit === "All" ? sales : sales.slice(0, rowLimit)).map((sale) => (
                    <React.Fragment key={sale._id}>
                      <tr
                        onClick={() => handleRowClick(sale)}
                        className="hover:bg-slate-50 cursor-pointer transition-colors duration-150"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-slate-900">{sale.salesAgent}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-slate-700">{sale.bookingType}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-slate-700">{sale.invoiceNumber || "N/A"}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                            {sale.numberOfTickets}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="text-sm font-bold text-slate-900">LKR {sale.totalPrice.toLocaleString()}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                            sale.paymentMethod === 'Cash' 
                              ? 'bg-green-100 text-green-800' 
                              : sale.paymentMethod === 'Card'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-purple-100 text-purple-800'
                          }`}>
                            {sale.paymentMethod}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                            sale.customerType === 'Foreigner' 
                              ? 'bg-orange-100 text-orange-800' 
                              : 'bg-slate-100 text-slate-800'
                          }`}>
                            {sale.customerType}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-slate-700">{sale.date}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-slate-500">{sale.time}</div>
                        </td>
                        {showComments && (
                          <td className="px-6 py-4">
                            <div className="text-sm text-slate-600 max-w-xs truncate">{sale.comments || "-"}</div>
                          </td>
                        )}
                      </tr>
                      {showUpdates && sale.updatedAt && (
                        <tr className="bg-amber-50">
                          <td colSpan={showComments ? 10 : 9} className="px-6 py-2">
                            <div className="text-xs text-amber-700 italic flex items-center gap-2">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Updated: {new Date(sale.updatedAt).toLocaleString("en-LK")}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-12 text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="bg-slate-100 p-6 rounded-full">
                <svg className="w-12 h-12 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">No Sales Data</h3>
                <p className="text-slate-500">No sales data found for the selected criteria. Try adjusting your filters or date range.</p>
              </div>
            </div>
          </div>
        )}

        {/* Modals remain the same with improved styling... */}
        {/* Share Modal */}
        {showShareModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
              <div className="p-6 border-b border-slate-200">
                <h3 className="text-xl font-semibold text-slate-800">Share This View</h3>
              </div>
              <div className="p-6">
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Share Link</label>
                  <input
                    type="text"
                    value={shareLink}
                    readOnly
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm bg-slate-50 focus:outline-none"
                  />
                </div>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setShowShareModal(false)}
                    className="px-6 py-3 border border-slate-300 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => navigator.clipboard.writeText(shareLink)}
                    className="px-6 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors duration-200 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Copy Link
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Update Modal */}
        {showUpdateModal && selectedSale && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-slate-200">
                <h3 className="text-xl font-semibold text-slate-800">Update Sale Record</h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-slate-700">Number of Tickets</label>
                    <input
                      type="number"
                      value={selectedSale.numberOfTickets}
                      onChange={(e) => setSelectedSale({
                        ...selectedSale,
                        numberOfTickets: parseInt(e.target.value),
                      })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-slate-700">Booking Type</label>
                    <select
                      value={selectedSale.bookingType}
                      onChange={(e) => {
                        setSelectedSale({
                          ...selectedSale,
                          bookingType: e.target.value,
                        });
                        if (e.target.value !== "Brought by Guide") {
                          setSelectedGuide("");
                        }
                      }}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500"
                    >
                      <option value="Walk-in (approach us)">Walk-in (approach us)</option>
                      <option value="Walk-in (approach by team)">Walk-in (approach by team)</option>
                      <option value="Online Booking">Online Booking</option>
                      <option value="Brought by Guide">Brought by Guide</option>
                      <option value="In-house">In-house</option>
                      <option value="Restaurant">Restaurant</option>
                      <option value="OTA">OTA</option>
                      <option value="DMC">DMC</option>
                      <option value="Other (Shops/Restaurants)">Other (Shops/Restaurants)</option>
                    </select>
                  </div>

                  {/* Guide Selection - Only show when booking type is "Brought by Guide" */}
                  {selectedSale.bookingType === "Brought by Guide" && (
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-slate-700">Select Guide *</label>
                      <div className="space-y-3">
                        {/* Search Input */}
                        <input
                          type="text"
                          placeholder="Search guides..."
                          value={guideSearchTerm}
                          onChange={(e) => setGuideSearchTerm(e.target.value)}
                          className="w-full px-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                        />
                        
                        {/* Guide Selection */}
                        <select
                          value={selectedGuide}
                          onChange={(e) => {
                            setSelectedGuide(e.target.value);
                            setSelectedSale({
                              ...selectedSale,
                              guideName: e.target.value
                            });
                          }}
                          className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500"
                          required
                        >
                          <option value="">
                            {guidesLoading ? "Loading guides..." : "Select a guide"}
                          </option>
                          {!guidesLoading && Array.isArray(guides) && guides.length > 0 ? (
                            guides
                              .filter(guide => {
                                if (!guide) return false;
                                const searchTerm = (guideSearchTerm || "").toLowerCase();
                                const guideName = (guide.guideName || "").toLowerCase();
                                const guideId = (guide.guideId || "").toLowerCase();
                                return guideName.includes(searchTerm) || guideId.includes(searchTerm);
                              })
                              .map((guide) => (
                                <option key={guide._id || Math.random()} value={guide.guideName || ""}>
                                  {guide.guideName || "Unknown"} - {guide.guideId || "No ID"}
                                </option>
                              ))
                          ) : !guidesLoading ? (
                            <option disabled>No guides available - Add some guides first</option>
                          ) : null}
                        </select>
                        
                        {/* Add New Guide Button */}
                        <button
                          type="button"
                          onClick={() => setShowAddGuideModal(true)}
                          className="w-full px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors duration-200 flex items-center justify-center gap-2 text-sm"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          {guides.length === 0 ? "Add First Guide" : "Add New Guide"}
                        </button>
                        
                        {guides.length === 0 && (
                          <div className="text-center p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <p className="text-xs text-yellow-700">
                              <strong>No guides available.</strong> Add your first guide to enable "Brought by Guide" bookings.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-slate-700">Payment Method</label>
                    <select
                      value={selectedSale.paymentMethod}
                      onChange={(e) => setSelectedSale({
                        ...selectedSale,
                        paymentMethod: e.target.value,
                      })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500"
                    >
                      <option value="Cash">Cash</option>
                      <option value="Card">Card</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-slate-700">Customer Type</label>
                    <select
                      value={selectedSale.customerType}
                      onChange={(e) => setSelectedSale({
                        ...selectedSale,
                        customerType: e.target.value,
                      })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500"
                    >
                      <option value="Foreigner">Foreigner</option>
                      <option value="Local">Local</option>
                    </select>
                  </div>

                  <div className="md:col-span-2 space-y-2">
                    <label className="block text-sm font-semibold text-slate-700">Comments</label>
                    <textarea
                      rows="4"
                      value={selectedSale.comments}
                      onChange={(e) => setSelectedSale({ ...selectedSale, comments: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500"
                      placeholder="Add any additional comments..."
                    />
                  </div>

                  {/* Security Code for Delete */}
                  <div className="md:col-span-2 space-y-2">
                    <label className="block text-sm font-semibold text-red-700">Security Code (Required for deletion)</label>
                    <input
                      type="password"
                      value={deleteConfirmCode}
                      onChange={(e) => setDeleteConfirmCode(e.target.value)}
                      className="w-full px-4 py-3 border border-red-300 rounded-xl text-sm focus:outline-none focus:ring-4 focus:ring-red-100 focus:border-red-500"
                      placeholder="Enter security code to enable delete"
                    />
                    <p className="text-xs text-red-600">*Required to delete sales records</p>
                  </div>
                </div>

                <div className="flex gap-3 justify-end mt-8 pt-6 border-t border-slate-200">
                  <button
                    onClick={() => setShowUpdateModal(false)}
                    className="px-6 py-3 border border-slate-300 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      if (deleteConfirmCode !== "Vrarchives@64") {
                        alert("Invalid security code. Please enter the correct code to delete this sale.");
                        return;
                      }
                      
                      const confirmDelete = window.confirm("Are you sure you want to delete this sale? This action cannot be undone.");
                      if (confirmDelete) {
                        axios
                          .delete(
                            `${process.env.REACT_APP_BACKEND_URL}/api/delete-sale/${selectedSale._id}`,
                            { headers: { Authorization: `Bearer ${token}` } }
                          )
                          .then(() => {
                            setShowUpdateModal(false);
                            setDeleteConfirmCode("");
                            fetchSales();
                            alert("Sale deleted successfully");
                          })
                          .catch((err) => {
                            console.error("Delete error:", err);
                            alert("Failed to delete sale: " + (err.response?.data?.message || err.message));
                          });
                      }
                    }}
                    disabled={deleteConfirmCode !== "Vrarchives@64"}
                    className={`px-6 py-3 rounded-xl transition-colors duration-200 flex items-center gap-2 ${
                      deleteConfirmCode === "Vrarchives@64" 
                        ? "bg-red-500 text-white hover:bg-red-600" 
                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                  </button>
                  <button
                    onClick={() => {
                      // Validate guide selection if booking type is "Brought by Guide"
                      if (selectedSale.bookingType === "Brought by Guide" && !selectedGuide) {
                        alert("Please select a guide when booking type is 'Brought by Guide'");
                        return;
                      }

                      const confirmUpdate = window.confirm("Are you sure you want to update this sale?");
                      if (confirmUpdate) {
                        const updateData = {
                          ...selectedSale,
                          guideName: selectedSale.bookingType === "Brought by Guide" ? selectedGuide : null
                        };

                        axios
                          .put(
                            `${process.env.REACT_APP_BACKEND_URL}/api/update-sale/${selectedSale._id}`,
                            updateData,
                            { headers: { Authorization: `Bearer ${token}` } }
                          )
                          .then(() => {
                            setShowUpdateModal(false);
                            setDeleteConfirmCode("");
                            fetchSales();
                            alert("Sale updated successfully");
                          })
                          .catch((err) => {
                            console.error("Update error:", err);
                            alert("Failed to update sale: " + (err.response?.data?.message || err.message));
                          });
                      }
                    }}
                    className="px-6 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors duration-200 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Update Sale
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Sale Modal */}
        {showAddSaleModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl h-[90vh] relative overflow-hidden">
              <div className="absolute top-4 right-4 z-10">
                <button
                  onClick={() => setShowAddSaleModal(false)}
                  className="bg-white hover:bg-gray-100 text-gray-600 hover:text-gray-800 rounded-full p-2 shadow-lg transition-colors duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <iframe
                src="/addSale"
                title="Add Sale"
                className="w-full h-full rounded-2xl"
                style={{ border: "none" }}
              />
            </div>
          </div>
        )}

        {/* Add Guide Modal */}
        {showAddGuideModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl h-[90vh] relative overflow-hidden">
              <div className="absolute top-4 right-4 z-10">
                <button
                  onClick={() => setShowAddGuideModal(false)}
                  className="bg-white hover:bg-gray-100 text-gray-600 hover:text-gray-800 rounded-full p-2 shadow-lg transition-colors duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <iframe
                src="/addGuide"
                title="Add Guide"
                className="w-full h-full rounded-2xl"
                style={{ border: "none" }}
              />
            </div>
          </div>
        )}

      </div>

      {/* Team Floating Navigation */}
      <TeamFloatingNavigation />
    </div>
  );
}

export default ViewSalePage;
